/**
 * ELMO returns position / department / location on a person as an **internal
 * reference**, not a name — e.g. `/positions/0a00d3f1-…`. The People Tracker
 * lands the value exactly as ELMO sent it, because resolving each one costs
 * another call against a rate limit shared across every credential.
 *
 * So a column bound straight to `PositionRef` would show a GUID. These helpers
 * tell the two apart, so the UI can print a real name when ELMO happens to send
 * one and say plainly that it did not when it doesn't — rather than either
 * dumping the reference on screen or silently rendering a blank cell, which
 * would read as "this person has no position".
 */

/** True when the value is an ELMO IRI reference (`/positions/…`) rather than a name. */
export function isElmoRef(value: string | null | undefined): boolean {
  if (!value) return false;
  return value.trim().startsWith('/');
}

/**
 * The display name for an ELMO reference field, or `null` when there is nothing
 * a human can read — either because ELMO sent no value at all, or because it
 * sent a reference. `null` is the caller's cue to render the "not a name"
 * treatment; the two cases are separated by {@link isElmoRef}.
 */
export function elmoRefLabel(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed || isElmoRef(trimmed)) return null;
  return trimmed;
}

/** How ELMO currently regards this record. */
export type ElmoActivity = 'Active' | 'Inactive' | 'Unknown';

/**
 * `null` is deliberately **Unknown**, not Inactive. An unpopulated flag is not
 * evidence that someone has left, and treating it as such is how a real starter
 * would disappear from the queue. Mirrors `DeriveQueueStatus` on the API side.
 */
export function elmoActivity(isActiveInElmo: boolean | null | undefined): ElmoActivity {
  if (isActiveInElmo === true) return 'Active';
  if (isActiveInElmo === false) return 'Inactive';
  return 'Unknown';
}
